import React, { useState, useEffect, useMemo } from 'react';
import { Check, X, Eye, Users, Trophy, Clock, UserCheck, UserPlus, BookOpen, Award, Mail, Pause, Ban, RefreshCw, FileText, Download, Edit } from 'lucide-react';


import api from '../../services/api';
import { teamAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useToast } from '../../contexts/ToastContext';
import { TeamWithDetails } from '../../types/events';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import TeamMembersManagement from './TeamMembersManagement';
import ProblemStatementsPanel from '../../components/admin/ProblemStatementsPanel';
import TeamFormingBar from '../../components/admin/TeamFormingBar';
import { uploadAPI } from '../../services/api';

interface Mentor {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: string;
  primarySkills?: string[];
  secondarySkills?: string[];
  skills?: {
    technical?: string[];
    soft?: string[];
  };
  experience?: {
    yearsOfExperience?: number;
  };
  education?: {
    graduationYear?: number;
  };
  professionalInfo?: {
    yearsOfExperience?: number;
  };
}

interface ProblemStatement {
  _id: string;
  title: string;
  description: string;
  category: string;
  difficulty: string;
}

interface AssignmentStats {
  problemStatementCounts: { [key: string]: number };
  mentorCounts: { [key: string]: number };
}

// A problem statement or a mentor is given to a team while it is still coming
// together — that is the point of giving it options to choose between. Only a
// team that has ended has nothing left to assign.
const canBeAssigned = (t: { status: string }) =>
  !['rejected', 'completed', 'dissolved'].includes(t.status);

const TeamManagement: React.FC = () => {

  
  const [teams, setTeams] = useState<TeamWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState<TeamWithDetails | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterApproval, setFilterApproval] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [problemStatements, setProblemStatements] = useState<ProblemStatement[]>([]);
  const [assignmentStats, setAssignmentStats] = useState<AssignmentStats>({ problemStatementCounts: {}, mentorCounts: {} });
  const [selectedMentor, setSelectedMentor] = useState<string>('');
  const [selectedProblemStatement, setSelectedProblemStatement] = useState<string>('');
  // The shortlist a team is being offered, as opposed to the one problem an
  // organiser is assigning outright. Both live on this screen because both are
  // things an organiser does.
  const [shortlist, setShortlist] = useState<string[]>([]);
  const [isScoringLoading, setIsScoringLoading] = useState<boolean>(false);

  const [activeTab, setActiveTab] = useState<string>('info');
  const [mainTab, setMainTab] = useState<string>('teams');
  // Shown by default. A rejected team is the one an organiser most needs to
  // look at — to see why it was rejected, or to delete it — and hiding it made
  // it look as though it had vanished.
  const [hideRejectedTeams, setHideRejectedTeams] = useState<boolean>(false);
  const [teamToDelete, setTeamToDelete] = useState<{id: string, name: string} | null>(null);
  const { success, error } = useToast();
  const [assigningMentor, setAssigningMentor] = useState<string | null>(null);
  const [removingMentor, setRemovingMentor] = useState<string | null>(null);
  const [approvingTeam, setApprovingTeam] = useState<string | null>(null);

  useEffect(() => {
    fetchTeams();
    fetchMentors();
    fetchAssignmentStats();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await teamAPI.getAllTeams();
      
      if (response.length > 0) {
        const firstTeam = response[0];
        
        // Set problem statements from the first team's hackathon data
        if (firstTeam.hackathon?.problemStatements) {
          setProblemStatements(firstTeam.hackathon.problemStatements);
        }
      }
      
      setTeams(response);
    } catch (err: any) {
      console.error('Failed to fetch teams:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMentors = async () => {
    try {
      const response = await teamAPI.getAvailableMentors();
      setMentors(response.data || []);
    } catch (err: any) {
      console.error('Failed to fetch mentors:', err);
    }
  };

  const fetchProblemStatements = async (hackathonId: string) => {
    try {
      const problemStatements = await teamAPI.getHackathonProblemStatements(hackathonId);
      setProblemStatements(problemStatements);
    } catch (err: any) {
      console.error('❌ Failed to fetch problem statements:', err);
    }
  };

  const fetchAssignmentStats = async () => {
    try {
      const stats = await teamAPI.getTeamAssignmentStats();
      setAssignmentStats(stats);
    } catch (err: any) {
      console.error('Failed to fetch assignment stats:', err);
    }
  };

  const handleApproveTeam = async (teamId: string) => {
    try {
      setApprovingTeam(teamId);
      await teamAPI.approveTeam(teamId);
      success('Team approved successfully');
      fetchTeams();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to approve team');
    } finally {
      setApprovingTeam(null);
    }
  };



  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [teamToReject, setTeamToReject] = useState<{id: string, name: string} | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [teamToDeactivate, setTeamToDeactivate] = useState<{id: string, name: string} | null>(null);
  const [showEditTeamNameDialog, setShowEditTeamNameDialog] = useState(false);
  const [teamToEdit, setTeamToEdit] = useState<{id: string, name: string} | null>(null);
  const [newTeamName, setNewTeamName] = useState('');

  const handleRejectTeamWithConfirmation = (teamId: string, teamName: string) => {
    setTeamToReject({ id: teamId, name: teamName });
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const handleEditTeamName = (team: TeamWithDetails) => {
    setTeamToEdit({ id: team._id, name: team.name });
    setNewTeamName(team.name);
    setShowEditTeamNameDialog(true);
  };

  const confirmEditTeamName = async () => {
    if (!teamToEdit || !newTeamName.trim()) {
      error('Please provide a valid team name');
      return;
    }

    if (newTeamName.trim() === teamToEdit.name) {
      setShowEditTeamNameDialog(false);
      setTeamToEdit(null);
      setNewTeamName('');
      return;
    }

    try {
      const result = await teamAPI.updateTeamName(teamToEdit.id, newTeamName.trim());
      success(`Team name updated from "${teamToEdit.name}" to "${newTeamName.trim()}"`);
      setShowEditTeamNameDialog(false);
      setTeamToEdit(null);
      setNewTeamName('');
      fetchTeams();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to update team name');
    }
  };

  const confirmRejectTeam = async () => {
    if (!teamToReject || !rejectionReason.trim()) {
      error('Please provide a reason for rejecting this team');
      return;
    }

    try {
      await teamAPI.rejectTeam(teamToReject.id, rejectionReason);
      success('Team rejected successfully');
      fetchTeams();
      fetchAssignmentStats();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to reject team');
    } finally {
      setShowRejectDialog(false);
      setTeamToReject(null);
      setRejectionReason('');
    }
  };

  const handleAssignMentor = async (teamId: string, mentorId: string) => {
    try {
      setAssigningMentor(teamId);
      const updatedTeam = await teamAPI.assignMentor(teamId, mentorId);
      success('Mentor assigned successfully');
      
      // Update the selectedTeam state immediately if the modal is open for this team
      if (selectedTeam && selectedTeam._id === teamId) {
        setSelectedTeam(updatedTeam);
      }
      
      fetchTeams();
      fetchAssignmentStats();
      setSelectedMentor('');
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to assign mentor');
    } finally {
      setAssigningMentor(null);
    }
  };

  const handleRemoveMentor = async (teamId: string) => {
    try {
      setRemovingMentor(teamId);
      await teamAPI.removeMentor(teamId);
      success('Mentor removed successfully');
      
      // Update the selectedTeam state immediately if the modal is open for this team
      if (selectedTeam && selectedTeam._id === teamId) {
        setSelectedTeam({
          ...selectedTeam,
          assignedMentor: undefined
        });
      }
      
      // Refresh the teams list and assignment stats
      fetchTeams();
      fetchAssignmentStats();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to remove mentor');
    } finally {
      setRemovingMentor(null);
    }
  };

  const handleDeactivateTeam = (teamId: string, teamName: string) => {
    setTeamToDeactivate({ id: teamId, name: teamName });
    setShowDeactivateDialog(true);
  };

  const confirmDeactivateTeam = async () => {
    if (!teamToDeactivate) return;
    
    try {
      await teamAPI.deactivateTeam(teamToDeactivate.id);
      success('Team deactivated successfully');
      fetchTeams();
      fetchAssignmentStats();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to deactivate team');
    } finally {
      setShowDeactivateDialog(false);
      setTeamToDeactivate(null);
    }
  };

  const handleAssignProblemStatement = async (teamId: string, problemStatement: ProblemStatement) => {
    try {
      console.log('Assigning problem statement:', { teamId, problemStatement });
      const updatedTeam = await teamAPI.assignProblemStatement(teamId, problemStatement);
      success('Problem statement assigned successfully');
      
      // Update the selectedTeam immediately for the modal
      if (selectedTeam && selectedTeam._id === teamId) {
        setSelectedTeam(updatedTeam);
      }
      
      fetchTeams();
      fetchAssignmentStats();
      setSelectedProblemStatement('');
    } catch (err: any) {
      console.error('Error assigning problem statement:', err);
      const errorMessage = err.response?.data?.message || 'Failed to assign problem statement';
      error(errorMessage);
    }
  };

  const handleScoreSubmission = async (teamId: string, modalScore: number, modalFeedback: string) => {
    if (modalScore < 0 || modalScore > 170) {
      error('Score must be between 0 and 170');
      return;
    }

    try {
      setIsScoringLoading(true);
      const updatedTeam = await teamAPI.scoreSubmission(teamId, modalScore, modalFeedback);
      success('Submission scored successfully');
      
      // Update both the teams list and the selected team
      await fetchTeams();
      setSelectedTeam(updatedTeam);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to score submission');
    } finally {
      setIsScoringLoading(false);
    }
  };





  const handleClearOffered = async (teamId: string) => {
    if (!window.confirm('Withdraw this team\'s shortlist? They will have nothing to choose between, and any choice they already made is dropped with it.')) return;
    try {
      const res = await teamAPI.clearOfferedProblemStatements(teamId);
      success(res.message);
      fetchTeams();
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not withdraw that shortlist');
    }
  };

  const handleClearAssigned = async (teamId: string) => {
    if (!window.confirm('Withdraw the problem statement this team was given? They will be left with none.')) return;
    try {
      const res = await teamAPI.clearAssignedProblemStatement(teamId);
      success(res.message);
      fetchTeams();
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not withdraw that problem statement');
    }
  };

  const handleUnlockProblemStatement = async (teamId: string) => {
    if (!window.confirm('Unlock this team\'s problem statement? You will be able to offer them a new shortlist, or assign a different problem.')) return;
    try {
      const res = await teamAPI.unlockProblemStatement(teamId);
      success(res.message);
      fetchTeams();
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not unlock that problem statement');
    }
  };

  const handleOfferProblemStatements = async (teamId: string, statementIds: string[]) => {
    try {
      const res = await teamAPI.offerProblemStatements(teamId, statementIds);
      success(res.message);
      // The limit is a default for the bulk allocation, not a rule binding an
      // organiser, so this is said rather than prevented.
      if (res.overruled?.length) {
        error(`Note: ${res.overruled.join('; ')}`);
      }
      fetchTeams();
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not offer those problem statements');
    }
  };

  const handleSetLeader = async (teamId: string, userId: string, memberName: string) => {
    if (!window.confirm(`Make ${memberName} the leader of this team? The current leader stays on as a member.`)) return;
    try {
      await teamAPI.setTeamLeader(teamId, userId);
      success(`${memberName} is now the team leader`);
      fetchTeams();
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not change the team leader');
    }
  };

  const handleRemoveMember = async (teamId: string, userId: string, memberName: string) => {
    if (!window.confirm(`Remove ${memberName} from this team? They will be free to join or start another one.`)) return;
    try {
      await teamAPI.removeMember(teamId, userId);
      success(`${memberName} removed from the team`);
      fetchTeams();
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not remove that member');
    }
  };

  const handleDeleteTeam = (teamId: string, teamName: string) => {
    setTeamToDelete({ id: teamId, name: teamName });
  };



  const confirmDeleteTeam = async () => {
    if (!teamToDelete) return;
    
    try {
      await teamAPI.deleteTeam(teamToDelete.id);
      success('Team deleted successfully!');
      fetchTeams();
      setTeamToDelete(null);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to delete team');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMentorAssignmentCount = (mentorId: string) => {
    return assignmentStats.mentorCounts[mentorId] || 0;
  };

  const getProblemStatementAssignmentCount = (problemStatementId: string) => {
    return assignmentStats.problemStatementCounts[problemStatementId] || 0;
  };

  const filteredTeams = teams.filter(team => {
    const matchesStatus = filterStatus === 'all' || team.status === filterStatus;
    const matchesApproval = filterApproval === 'all' || 
      (filterApproval === 'approved' && team.status === 'approved') ||
      (filterApproval === 'pending' && team.status === 'pending_approval');
    const matchesSearch = team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (team.hackathon?.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         `${team.leader?.firstName} ${team.leader?.lastName}`.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Hide rejected teams if the option is enabled
    const matchesRejectedFilter = !hideRejectedTeams || team.status !== 'rejected';
    
    return matchesStatus && matchesApproval && matchesSearch && matchesRejectedFilter;
  });

  const openTeamModal = (team: TeamWithDetails) => {
    setSelectedTeam(team);
    setShowViewModal(true);
    setActiveTab('info');
    // Use problem statements from the team's hackathon data
    if (team.hackathon?.problemStatements) {
      setProblemStatements(team.hackathon.problemStatements);
    } else if (team.hackathon?._id) {
      // Fallback: fetch problem statements if not populated
      fetchProblemStatements(team.hackathon._id);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
          <p className="text-gray-600">Review and manage hackathon teams and members</p>
        </div>
        <Button
          onClick={() => {
            fetchTeams();
            fetchAssignmentStats();
          }}
          variant="outline"
          className="flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Main Tabs */}
      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="teams" className="text-sm font-medium">Teams Management</TabsTrigger>
          <TabsTrigger value="members" className="text-sm font-medium">Team Members Management</TabsTrigger>
          <TabsTrigger value="problems" className="text-sm font-medium">Problem Statements</TabsTrigger>
        </TabsList>

        <TabsContent value="teams" className="mt-6">
          {/* Getting teams into existence — everything below reviews teams
              that already exist. */}
          <TeamFormingBar onChanged={fetchTeams} />

          {/* Teams Management Content */}
          <div>
            {/* Filters */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <input
                    type="text"
                    placeholder="Search teams..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="forming">Forming</option>
                    <option value="pending_approval">Pending Approval</option>
                    <option value="approved">Approved</option>
                    <option value="active">Active</option>
                    <option value="submitted">Submitted</option>
                    <option value="completed">Completed</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div>
                  <select
                    value={filterApproval}
                    onChange={(e) => setFilterApproval(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Approval</option>
                    <option value="pending">Pending Approval</option>
                    <option value="approved">Approved</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Total Teams: {teams.length}
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="hideRejected"
                      checked={hideRejectedTeams}
                      onChange={(e) => setHideRejectedTeams(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="hideRejected" className="text-sm text-gray-700">
                      Hide Rejected
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Teams Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTeams.map((team) => (
          <Card key={team._id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  <CardTitle className="text-lg">{team.name}</CardTitle>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <StatusBadge status={team.status} />
                  <Badge className={team.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {team.isActive ? 'ACTIVE' : 'INACTIVE'}
                  </Badge>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                Hackathon: {team.hackathon?.title || 'No hackathon assigned'}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-4">
                <div className="flex items-center text-sm text-gray-500">
                  <Users className="h-4 w-4 mr-2" />
                  <span>{team.members.length}/{team.hackathon?.maxTeamSize || 'N/A'} members</span>
                </div>
                
                <div className="text-sm">
                  <strong>Leader:</strong> {team.leader?.firstName} {team.leader?.lastName}
                </div>
                
                <div className="text-sm text-gray-600">
                  <strong>Problem:</strong> {team.assignedProblemStatement?.title || team.selectedProblemStatement?.title || 'Not assigned'}
                </div>

                {team.assignedMentor && (
                  <div className="text-sm text-gray-600">
                    <strong>Mentor:</strong> {team.assignedMentor.firstName} {team.assignedMentor.lastName}
                  </div>
                )}
                
                <div className="text-sm text-gray-500">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Created: {formatDate(team.createdAt)}
                </div>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex gap-2">
                  {/* Quick Actions */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedTeam(team);
                      setShowViewModal(true);
                    }}
                    className="text-blue-600 hover:text-blue-700"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  
                  {/* Edit Team Name Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditTeamName(team)}
                    className="text-purple-600 hover:text-purple-700"
                    title="Edit Team Name"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  
                  {/* Add Member Button */}
                  {team.members.length < (team.hackathon?.maxTeamSize || 0) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/teams/${team._id}/add-members`, '_blank')}
                      className="text-green-600 hover:text-green-700"
                      title="Add Member"
                    >
                      <UserPlus className="h-4 w-4" />
                    </Button>
                  )}
                  
                  {team.status === 'pending_approval' && (
                    <Button
                      size="sm"
                      onClick={() => handleApproveTeam(team._id)}
                      className="bg-green-600 hover:bg-green-700"
                      title="Approve Team"
                      disabled={approvingTeam === team._id}
                    >
                      {approvingTeam === team._id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                  
                  {/* Single Reject Button with Confirmation */}
                  {team.status !== 'rejected' && team.status !== 'completed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRejectTeamWithConfirmation(team._id, team.name)}
                      className="text-red-600 hover:text-red-700"
                      title="Reject Team"
                    >
                      <Ban className="h-4 w-4" />
                    </Button>
                  )}
                  
                  {/* Deactivate Button for Active Teams */}
                  {team.isActive && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeactivateTeam(team._id, team.name)}
                      className="text-orange-600 hover:text-orange-700 border-orange-600"
                      title="Deactivate Team"
                    >
                      <Pause className="h-4 w-4" />
                    </Button>
                  )}
                  
                  {/* Deleting is not only for rejected teams: a team formed by
                      mistake, or one abandoned mid-hackathon, has to be
                      removable too. Its members go back to the pool. */}
                  {team.status !== 'dissolved' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteTeam(team._id, team.name)}
                      className="text-red-600 hover:text-red-700 border-red-600"
                      title="Delete team and release its members"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                  
                  {canBeAssigned(team) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedTeam(team);
                        setActiveTab('assignments');
                        setShowViewModal(true);
                      }}
                      className="text-purple-600 hover:text-purple-700"
                      title={team.assignedMentor ? "Change mentor" : "Assign mentor"}
                    >
                      <UserPlus className="h-4 w-4" />
                    </Button>
                  )}

                  {canBeAssigned(team) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedTeam(team);
                        setShowViewModal(true);
                      }}
                      className="text-green-600 hover:text-green-700"
                      title={team.assignedProblemStatement ? "Change problem statement" : "Assign problem statement"}
                    >
                      <BookOpen className="h-4 w-4" />
                    </Button>
                  )}

                  {team.submission && !team.submissionScore && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedTeam(team);
                        setShowViewModal(true);
                      }}
                      className="text-purple-600 hover:text-purple-700"
                      title="Score Submission"
                    >
                      <Award className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredTeams.length === 0 && (
        <div className="text-center py-12">
          <Trophy className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No teams found</h3>
          <p className="text-gray-600">
            {searchTerm || filterStatus !== 'all' || filterApproval !== 'all'
              ? 'Try adjusting your search or filters'
              : 'No teams have been created yet'
            }
          </p>
        </div>
      )}
          </div>
        </TabsContent>

        <TabsContent value="members" className="mt-6">
          <TeamMembersManagement />
        </TabsContent>

        <TabsContent value="problems" className="mt-6">
          <ProblemStatementsPanel />
        </TabsContent>
      </Tabs>

      {/* Enhanced Team Modal */}
      {showViewModal && selectedTeam && (
        <EnhancedTeamModal
          team={selectedTeam}
          key={selectedTeam._id} // Force re-render when team changes
          mentors={mentors}
          problemStatements={problemStatements}
          assignmentStats={assignmentStats}
          onClose={() => {
            setShowViewModal(false);
            setSelectedTeam(null);
            setActiveTab('info');
          }}
          onToggleResubmission={async (teamId: string) => {
            try {
              console.log('🔄 Toggling resubmission for team:', teamId);
              const response = await teamAPI.toggleResubmission(teamId);
              console.log('✅ Toggle response:', response);
              await fetchTeams();
              console.log('✅ Teams refreshed');
              
              // Update the selectedTeam with the new data
              setSelectedTeam(response);
              console.log('✅ SelectedTeam updated with allowResubmission:', response.allowResubmission);
            } catch (err: any) {
              console.error('❌ Failed to toggle resubmission:', err);
            }
          }}
          onApprove={() => handleApproveTeam(selectedTeam._id)}
          onAssignMentor={handleAssignMentor}
          onRemoveMentor={handleRemoveMentor}
          onDeactivateTeam={handleDeactivateTeam}
          onAssignProblemStatement={handleAssignProblemStatement}
          onScoreSubmission={handleScoreSubmission}
          onDeleteTeam={handleDeleteTeam}
          onSetLeader={handleSetLeader}
          onOfferProblemStatements={handleOfferProblemStatements}
          onUnlockProblemStatement={handleUnlockProblemStatement}
          onClearOffered={handleClearOffered}
          onClearAssigned={handleClearAssigned}
          onRemoveMember={handleRemoveMember}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          assigningMentor={assigningMentor}
          removingMentor={removingMentor}
          approvingTeam={approvingTeam}
          onDownloadFile={async (fileKey: string) => {
            try {
              const response = await uploadAPI.downloadSubmissionFile(fileKey);
              const blob = new Blob([response], { type: 'application/octet-stream' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = fileKey.substring(fileKey.lastIndexOf('/') + 1);
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
              success('File downloaded successfully!');
            } catch (err: any) {
              error(err.response?.data?.message || 'Failed to download file');
            }
          }}
          isScoringLoading={isScoringLoading}
        />
      )}

      {/* Reject Team Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Team</DialogTitle>
            <DialogDescription>
              Are you sure you want to reject the team "{teamToReject?.name}"? This action cannot be undone and will remove the team from the hackathon.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="rejection-reason">Reason for rejection *</Label>
              <Textarea
                id="rejection-reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a detailed reason for rejecting this team..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmRejectTeam}>
              Reject Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Deactivate Team Dialog */}
      <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate Team</DialogTitle>
            <DialogDescription>
              Are you sure you want to deactivate the team "{teamToDeactivate?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeactivateDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeactivateTeam}>
              Deactivate Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Team Dialog */}
      <Dialog open={!!teamToDelete} onOpenChange={(open) => !open && setTeamToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Team</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete the team "{teamToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTeamToDelete(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteTeam}>
              Delete Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Team Name Dialog */}
      <Dialog open={showEditTeamNameDialog} onOpenChange={setShowEditTeamNameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team Name</DialogTitle>
            <DialogDescription>
              Update the name for team "{teamToEdit?.name}".
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="team-name">Team Name *</Label>
              <Input
                id="team-name"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="Enter new team name..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditTeamNameDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmEditTeamName}>
              Update Team Name
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Enhanced Team Modal Component
const EnhancedTeamModal: React.FC<{
  team: TeamWithDetails;
  mentors: Mentor[];
  problemStatements: ProblemStatement[];
  assignmentStats: AssignmentStats;
  onClose: () => void;
  onApprove: () => void;
  onAssignMentor: (teamId: string, mentorId: string) => void;
  onRemoveMentor: (teamId: string) => void;
  onDeactivateTeam: (teamId: string, teamName: string) => void;
  onAssignProblemStatement: (teamId: string, problemStatement: ProblemStatement) => void;
  onScoreSubmission: (teamId: string, score: number, feedback: string) => void;
  onDeleteTeam: (teamId: string, teamName: string) => void;
  onSetLeader: (teamId: string, userId: string, memberName: string) => void;
  onOfferProblemStatements: (teamId: string, statementIds: string[]) => void;
  onUnlockProblemStatement: (teamId: string) => void;
  onClearOffered: (teamId: string) => void;
  onClearAssigned: (teamId: string) => void;
  onRemoveMember: (teamId: string, userId: string, memberName: string) => void;
  onToggleResubmission: (teamId: string) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  assigningMentor: string | null;
  removingMentor: string | null;
  approvingTeam: string | null;
  onDownloadFile: (fileKey: string) => void;
  isScoringLoading: boolean;
}> = ({ 
  team, 
  mentors, 
  problemStatements, 
  assignmentStats, 
  onClose, 
  onApprove, 
  onAssignMentor,
  onRemoveMentor,
  onDeactivateTeam,
  onAssignProblemStatement,
  onScoreSubmission,
  onDeleteTeam,
  onSetLeader,
  onOfferProblemStatements,
  onUnlockProblemStatement,
  onClearOffered,
  onClearAssigned,
  onRemoveMember,
  onToggleResubmission,
  activeTab,
  setActiveTab,
  assigningMentor,
  removingMentor,
  approvingTeam,
  onDownloadFile,
  isScoringLoading
}) => {
  const [selectedMentor, setSelectedMentor] = useState<string>('');
  const [selectedProblemStatement, setSelectedProblemStatement] = useState<string>('');
  // The shortlist a team is being offered, as opposed to the one problem an
  // organiser is assigning outright. Both live on this screen because both are
  // things an organiser does.
  const [shortlist, setShortlist] = useState<string[]>([]);
  const [submissionScore, setSubmissionScore] = useState<number>(0);
  const [submissionFeedback, setSubmissionFeedback] = useState<string>('');
  const [mentorSearchTerm, setMentorSearchTerm] = useState<string>('');
  const [isMentorDropdownOpen, setIsMentorDropdownOpen] = useState<boolean>(false);
  const [isEditingScore, setIsEditingScore] = useState<boolean>(false);
  const [scoringFiles, setScoringFiles] = useState<File[]>([]);

  const getMentorAssignmentCount = (mentorId: string) => {
    return assignmentStats.mentorCounts[mentorId] || 0;
  };

  const getProblemStatementAssignmentCount = (problemStatementId: string) => {
    return assignmentStats.problemStatementCounts[problemStatementId] || 0;
  };



  // Load existing score data when component mounts or team changes
  useEffect(() => {
    if (team && team.submissionScore) {
      const score = team.submissionScore.score || 0;
      const feedback = team.submissionScore.feedback || '';
      
      setSubmissionScore(score);
      setSubmissionFeedback(feedback);
    } else {
      setSubmissionScore(0);
      setSubmissionFeedback('');
    }
    setScoringFiles([]);
  }, [team?._id, team?.submissionScore]);



  // Helper function to get city from address
  const getCityFromAddress = (address?: string) => {
    if (!address) return 'N/A';
    
    // Split by commas and get the city (usually the second to last part)
    const parts = address.split(',').map(part => part.trim());
    
    // Look for common city patterns
    for (let i = parts.length - 1; i >= 0; i--) {
      const part = parts[i];
      // Skip if it's a postal code (numbers only) or country
      if (/^\d+$/.test(part) || part.toLowerCase() === 'india') {
        continue;
      }
      // If it's a state, the previous part is likely the city
      if (part.toLowerCase() === 'telangana' && i > 0) {
        return parts[i - 1];
      }
      // If it's not a state or country, it might be the city
      if (part.toLowerCase() !== 'telangana' && part.toLowerCase() !== 'india') {
        return part;
      }
    }
    
    // Fallback: return the second to last part
    return parts.length >= 2 ? parts[parts.length - 2] : 'N/A';
  };

  // Helper function to format user skills
  const getUserSkills = (user: any) => {
    const skills = [];
    
    // Add primary skills
    if (user.primarySkills && Array.isArray(user.primarySkills) && user.primarySkills.length > 0) {
      skills.push(...user.primarySkills);
    }
    
    // Add secondary skills
    if (user.secondarySkills && Array.isArray(user.secondarySkills) && user.secondarySkills.length > 0) {
      skills.push(...user.secondarySkills);
    }
    
    // Add technical skills from skills object
    if (user.skills?.technical && Array.isArray(user.skills.technical) && user.skills.technical.length > 0) {
      skills.push(...user.skills.technical);
    }
    
    // Add soft skills from skills object
    if (user.skills?.soft && Array.isArray(user.skills.soft) && user.skills.soft.length > 0) {
      skills.push(...user.skills.soft);
    }
    
    // Remove duplicates and limit to 5 skills
    const uniqueSkills = [...new Set(skills)];
    return uniqueSkills.slice(0, 5);
  };

  // Helper function to get user experience
  const getUserExperience = (user: any) => {
    if (user.professionalInfo?.yearsOfExperience) {
      return `${user.professionalInfo.yearsOfExperience} years`;
    }
    if (user.education?.graduationYear) {
      const currentYear = new Date().getFullYear();
      const yearsSinceGraduation = currentYear - user.education.graduationYear;
      if (yearsSinceGraduation >= 0) {
        return yearsSinceGraduation === 0 ? '' : `${yearsSinceGraduation} years`;
      }
    }
    return '';
  };

  // Filter and sort mentors with client-side search
  const filteredAndSortedMentors = useMemo(() => {
    let filtered = mentors;
    
    // Filter by search term
    if (mentorSearchTerm.trim()) {
      const searchTerm = mentorSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(mentor =>
        mentor.firstName.toLowerCase().includes(searchTerm) ||
        mentor.lastName.toLowerCase().includes(searchTerm) ||
        mentor.email.toLowerCase().includes(searchTerm)
      );
    }
    
    // Sort alphabetically by first name, then last name
    return filtered.sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [mentors, mentorSearchTerm]);



  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold">{team.name}</h2>
            <p className="text-gray-600">Hackathon: {team.hackathon?.title || 'No hackathon assigned'}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={team.status} />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="info">Team Info</TabsTrigger>
            <TabsTrigger value="assignments">Mentor</TabsTrigger>
            <TabsTrigger value="problem-statement">Problem Statement</TabsTrigger>
            <TabsTrigger value="scoring">Scoring</TabsTrigger>
            <TabsTrigger value="submission">Submission</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Team Information */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Team Information</h3>
                <div className="space-y-3">
                  <div>
                    <strong>Join Code:</strong> {team.joinCode}
                  </div>
                  <div>
                    <strong>Created:</strong> {new Date(team.createdAt).toLocaleString()}
                  </div>
                  <div>
                    <strong>Members:</strong> {team.members.length}/{team.hackathon?.maxTeamSize || 'N/A'}
                  </div>
                </div>

                <h4 className="font-medium mt-4 mb-2">Team Members</h4>
                <p className="text-xs text-gray-500 mb-2">
                  Removing someone frees them to join or start another team. Making
                  a member the leader moves the current leader down to an ordinary
                  member; nobody leaves the team.
                </p>
                <div className="space-y-3">
                  {team.members.map((member) => {
                    const isActualLeader = team.leader._id === member.user._id;
                    const displayRole = isActualLeader ? 'leader' : 'member';
                    const userSkills = getUserSkills(member.user);
                    

                    
                    // Get user experience
                    const getUserExperience = (user: any) => {
                      if (user.professionalInfo?.yearsOfExperience) {
                        return `${user.professionalInfo.yearsOfExperience} years`;
                      }
                      if (user.education?.graduationYear) {
                        const currentYear = new Date().getFullYear();
                        const yearsSinceGraduation = currentYear - user.education.graduationYear;
                        if (yearsSinceGraduation >= 0) {
                          return yearsSinceGraduation === 0 ? 'Student' : `${yearsSinceGraduation} years`;
                        }
                      }
                      return 'Student';
                    };
                    
                    return (
                      <div key={member.user._id} className="p-4 bg-gray-50 rounded-lg border">
                        <div className="flex items-center justify-between mb-3">
                          <div className="font-semibold text-lg text-gray-900">{member.user.firstName} {member.user.lastName}</div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">
                              {displayRole}
                            </span>
                            {!isActualLeader && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => onSetLeader(team._id, member.user._id, `${member.user.firstName} ${member.user.lastName}`)}
                                  title="Make this member the team leader"
                                >
                                  Make leader
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 border-red-600"
                                  onClick={() => onRemoveMember(team._id, member.user._id, `${member.user.firstName} ${member.user.lastName}`)}
                                  title="Remove from team"
                                >
                                  Remove
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div className="space-y-2">
                            <div className="flex items-center text-gray-700">
                              <Mail className="h-4 w-4 mr-2 text-gray-500" />
                              <span className="font-medium">Email:</span>
                              <span className="ml-1 break-all">{member.user.email}</span>
                            </div>
                            <div className="flex items-center text-gray-700">
                              <svg className="h-4 w-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              <span className="font-medium">Phone:</span>
                              <span className="ml-1">{member.user.phone || 'N/A'}</span>
                            </div>
                            <div className="flex items-center text-gray-700">
                              <svg className="h-4 w-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span className="font-medium">City:</span>
                              <span className="ml-1">{getCityFromAddress(member.user.address)}</span>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex items-start text-gray-700">
                              <svg className="h-4 w-4 mr-2 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                              <div>
                                <span className="font-medium">Experience:</span>
                                <span className="ml-1">{getUserExperience(member.user)}</span>
                              </div>
                            </div>
                            {userSkills.length > 0 && (
                              <div className="flex items-start text-gray-700">
                                <svg className="h-4 w-4 mr-2 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                </svg>
                                <div>
                                  <span className="font-medium">Skills:</span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {userSkills.map((skill, index) => (
                                      <span key={index} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                        {skill}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Problem Statement */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Problem Statement</h3>
                {team.assignedProblemStatement ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                      <h5 className="font-semibold text-green-800 mb-2">Assigned Problem Statement</h5>
                      <div><strong>Title:</strong> {team.assignedProblemStatement.title}</div>
                      <div><strong>Category:</strong> {team.assignedProblemStatement.category}</div>
                      <div><strong>Difficulty:</strong> {team.assignedProblemStatement.difficulty}</div>
                      <div><strong>Description:</strong></div>
                      <p className="text-gray-600 mt-1">{team.assignedProblemStatement.description}</p>
                    </div>
                    {team.selectedProblemStatement && team.selectedProblemStatement._id !== team.assignedProblemStatement._id && (
                      <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
                        <h5 className="font-semibold text-gray-800 mb-2">Originally Selected</h5>
                        <div><strong>Title:</strong> {team.selectedProblemStatement.title}</div>
                        <div><strong>Category:</strong> {team.selectedProblemStatement.category}</div>
                        <div><strong>Difficulty:</strong> {team.selectedProblemStatement.difficulty}</div>
                        <div><strong>Description:</strong></div>
                        <p className="text-gray-600 mt-1">{team.selectedProblemStatement.description}</p>
                      </div>
                    )}
                  </div>
                ) : team.selectedProblemStatement ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <h5 className="font-semibold text-yellow-800 mb-2">Selected Problem Statement (Pending Assignment)</h5>
                      <div><strong>Title:</strong> {team.selectedProblemStatement.title}</div>
                      <div><strong>Category:</strong> {team.selectedProblemStatement.category}</div>
                      <div><strong>Difficulty:</strong> {team.selectedProblemStatement.difficulty}</div>
                      <div><strong>Description:</strong></div>
                      <p className="text-gray-600 mt-1">{team.selectedProblemStatement.description}</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <p className="text-gray-600">No problem statement selected or assigned</p>
                  </div>
                )}

                {/* Mentor Information */}
                <h4 className="font-medium mt-6 mb-2">Assigned Mentor</h4>
                {team.assignedMentor ? (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-semibold text-lg text-gray-900">{team.assignedMentor.firstName} {team.assignedMentor.lastName}</div>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">
                        mentor
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="space-y-2">
                        <div className="flex items-center text-gray-700">
                          <Mail className="h-4 w-4 mr-2 text-gray-500" />
                          <span className="font-medium">Email:</span>
                          <span className="ml-1 break-all">{team.assignedMentor.email}</span>
                        </div>
                        {(team.assignedMentor as any).phone && (
                          <div className="flex items-center text-gray-700">
                            <svg className="h-4 w-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span className="font-medium">Phone:</span>
                            <span className="ml-1">{(team.assignedMentor as any).phone}</span>
                          </div>
                        )}
                        {getCityFromAddress((team.assignedMentor as any).address) && (
                          <div className="flex items-center text-gray-700">
                            <svg className="h-4 w-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="font-medium">City:</span>
                            <span className="ml-1">{getCityFromAddress((team.assignedMentor as any).address)}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        {getUserExperience(team.assignedMentor as any) && (
                          <div className="flex items-start text-gray-700">
                            <svg className="h-4 w-4 mr-2 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                            <div>
                              <span className="font-medium">Experience:</span>
                              <span className="ml-1">{getUserExperience(team.assignedMentor as any)}</span>
                            </div>
                          </div>
                        )}
                        {getUserSkills(team.assignedMentor as any).length > 0 && (
                          <div className="flex items-start text-gray-700">
                            <svg className="h-4 w-4 mr-2 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                            </svg>
                            <div>
                              <span className="font-medium">Skills:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {getUserSkills(team.assignedMentor as any).map((skill, index) => (
                                  <span key={index} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <p className="text-gray-600 text-sm">No mentor assigned yet</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="assignments" className="mt-6">
            <div className="space-y-6">
              {/* Mentor Assignment */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Mentor Assignment</h3>
                {team.assignedMentor ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <div className="font-medium">
                        Current Mentor: {team.assignedMentor.firstName} {team.assignedMentor.lastName}
                      </div>
                      <div className="text-sm text-gray-600">{team.assignedMentor.email}</div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => onRemoveMentor(team._id)}
                      className="text-red-600 hover:text-red-700"
                      disabled={removingMentor === team._id}
                    >
                      {removingMentor === team._id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                          Removing...
                        </>
                      ) : (
                        <>
                          <Ban className="h-4 w-4 mr-2" />
                          Remove Mentor
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Label htmlFor="mentor-select">Select Mentor</Label>
                    <Select 
                      value={selectedMentor} 
                      onValueChange={setSelectedMentor} 
                      disabled={assigningMentor === team._id}
                      open={isMentorDropdownOpen}
                      onOpenChange={setIsMentorDropdownOpen}
                    >
                                              <SelectTrigger onClick={() => setIsMentorDropdownOpen(true)}>
                          <SelectValue placeholder="Choose a mentor..." />
                        </SelectTrigger>
                      <SelectContent className="max-h-60" onPointerDownOutside={(e) => e.preventDefault()}>
                        {/* Search Input within dropdown */}
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Search mentors by name or email..."
                              value={mentorSearchTerm}
                              onChange={(e) => {
                                setMentorSearchTerm(e.target.value);
                                setIsMentorDropdownOpen(true);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              onFocus={(e) => e.target.select()}
                            />
                            {mentorSearchTerm && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMentorSearchTerm('');
                                  setSelectedMentor('');
                                  setIsMentorDropdownOpen(true);
                                }}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {filteredAndSortedMentors.length === 0 ? (
                          <div className="px-2 py-4 text-center text-gray-500">
                            No mentors found matching your search
                          </div>
                        ) : (
                          filteredAndSortedMentors.map((mentor) => {
                            const assignmentCount = getMentorAssignmentCount(mentor._id);
                            return (
                              <SelectItem key={mentor._id} value={mentor._id}>
                                <div className={`flex items-center justify-between w-full ${assignmentCount > 0 ? 'bg-blue-50' : ''}`}>
                                  <div className="flex items-center">
                                    <span className="font-medium">{mentor.firstName} {mentor.lastName}</span>
                                    <span className="text-sm text-gray-500 ml-2">({mentor.email})</span>
                                  </div>
                                  <div className="flex items-center">
                                    {assignmentCount > 0 ? (
                                      <Badge className="ml-2 bg-blue-100 text-blue-800 font-medium">
                                        {assignmentCount} team{assignmentCount !== 1 ? 's' : ''}
                                      </Badge>
                                    ) : (
                                      <span className="text-sm text-gray-400 ml-2">(0 teams)</span>
                                    )}
                                  </div>
                                </div>
                              </SelectItem>
                            );
                          })
                        )}
                      </SelectContent>
                    </Select>

                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="problem-statement" className="mt-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Problem Statement Management</h3>
                
                {/* Team Status Display */}
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Team Status:</span>
                    <Badge className={team.status === 'approved' ? 'bg-green-100 text-green-800' : 
                                     team.status === 'active' ? 'bg-blue-100 text-blue-800' :
                                     team.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' :
                                     team.status === 'forming' ? 'bg-gray-100 text-gray-800' :
                                     'bg-red-100 text-red-800'}>
                      {team.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                </div>
                
                {/* Current Problem Statement Display */}
                <div className="mb-6">
                  <h4 className="font-medium mb-3">Current Problem Statement</h4>
                  {team.assignedProblemStatement ? (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-semibold text-green-800">Assigned Problem Statement</h5>
                        <Badge className="bg-green-100 text-green-800">Currently Assigned</Badge>
                      </div>
                      <div className="space-y-2">
                        <div><strong>Title:</strong> {team.assignedProblemStatement.title}</div>
                        <div><strong>Category:</strong> {team.assignedProblemStatement.category}</div>
                        <div><strong>Difficulty:</strong> {team.assignedProblemStatement.difficulty}</div>
                        <div><strong>Description:</strong></div>
                        <p className="text-gray-700 mt-1 text-sm leading-relaxed">{team.assignedProblemStatement.description}</p>
                      </div>
                    </div>
                  ) : team.selectedProblemStatement ? (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-semibold text-yellow-800">Selected by Team</h5>
                        <Badge className="bg-yellow-100 text-yellow-800">Pending Assignment</Badge>
                      </div>
                      <div className="space-y-2">
                        <div><strong>Title:</strong> {team.selectedProblemStatement.title}</div>
                        <div><strong>Category:</strong> {team.selectedProblemStatement.category}</div>
                        <div><strong>Difficulty:</strong> {team.selectedProblemStatement.difficulty}</div>
                        <div><strong>Description:</strong></div>
                        <p className="text-gray-700 mt-1 text-sm leading-relaxed">{team.selectedProblemStatement.description}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                      <p className="text-gray-600">No problem statement selected or assigned</p>
                    </div>
                  )}
                </div>

                {/* Problem Statement Reassignment */}
                <div className="space-y-4">
                  <h4 className="font-medium">Assign Problem Statement</h4>
                  
                  {/* Status-based restrictions */}
                  {team.status === 'submitted' || team.status === 'completed' ? (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-red-700 text-sm">
                        <strong>Cannot reassign:</strong> Team has already submitted their project or completed the hackathon.
                      </p>
                    </div>
                  ) : team.status === 'active' ? (
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-md">
                      <p className="text-orange-700 text-sm">
                        <strong>Warning:</strong> Team is currently active. Reassigning may disrupt their progress.
                      </p>
                    </div>
                  ) : null}

                  {team.assignedProblemStatement?.title && (
                    <div className="mb-6 rounded-lg border border-gray-300 bg-gray-50 p-4">
                      <p className="font-medium text-gray-900">
                        Assigned: {team.assignedProblemStatement.title}
                      </p>
                      <p className="mt-1 text-xs text-gray-600">
                        Withdrawing leaves this team with no problem statement at all. To give
                        them a different one instead, assign it directly — that replaces this.
                      </p>
                      <button
                        type="button"
                        onClick={() => onClearAssigned(team._id)}
                        className="mt-2 rounded-md border border-gray-400 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Withdraw this problem statement
                      </button>
                    </div>
                  )}

                  {team.problemStatementLockedAt && (
                    <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
                      <p className="font-medium text-amber-900">This team's problem statement is locked</p>
                      <p className="mt-1 text-xs text-amber-800">
                        Locked when it was assigned, so the team cannot change it and no new
                        shortlist can be offered. Unlocking leaves the problem where it is —
                        it only makes it changeable again.
                      </p>
                      <button
                        type="button"
                        onClick={() => onUnlockProblemStatement(team._id)}
                        className="mt-2 rounded-md border border-amber-600 px-3 py-1.5 text-sm text-amber-800 hover:bg-amber-100"
                      >
                        Unlock
                      </button>
                    </div>
                  )}

                  <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4">
                    <p className="font-medium text-green-900">Offer a shortlist for the team to choose from</p>
                    <p className="mt-1 text-xs text-green-800">
                      Tick the problems this team may choose between. They pick one, then you
                      lock it in. The count beside each shows how many teams already hold it.
                    </p>

                    {team.offeredProblemStatements?.length ? (
                      <div className="mt-2">
                        <p className="text-xs text-green-900">
                          Currently offered {team.offeredProblemStatements.length}:{' '}
                          {team.offeredProblemStatements.map((o: any) => o.title).join(', ')}
                        </p>
                        <button
                          type="button"
                          onClick={() => onClearOffered(team._id)}
                          className="mt-2 rounded-md border border-gray-400 px-3 py-1 text-xs text-gray-700 hover:bg-gray-100"
                        >
                          Withdraw this shortlist
                        </button>
                      </div>
                    ) : null}

                    <div className="mt-3 max-h-56 space-y-2 overflow-y-auto">
                      {problemStatements.map((ps) => (
                        <label key={ps._id} className="flex items-start gap-2 text-sm">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={shortlist.includes(ps._id)}
                            onChange={(e) => setShortlist((prev) =>
                              e.target.checked ? [...prev, ps._id] : prev.filter((id) => id !== ps._id)
                            )}
                          />
                          <span>
                            <span className="font-medium">{ps.title}</span>
                            <span className="block text-xs text-gray-600">
                              {ps.category} • {ps.difficulty} • {getProblemStatementAssignmentCount(ps._id)} team(s) assigned
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>

                    <button
                      type="button"
                      disabled={!shortlist.length}
                      onClick={() => { onOfferProblemStatements(team._id, shortlist); setShortlist([]); }}
                      className="mt-3 rounded-md border border-green-600 px-3 py-1.5 text-sm text-green-700 hover:bg-green-100 disabled:opacity-50"
                    >
                      Offer {shortlist.length || ''} to this team
                    </button>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="problem-reassign">Or assign one outright</Label>
                    <Select value={selectedProblemStatement} onValueChange={setSelectedProblemStatement}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a new problem statement..." />
                      </SelectTrigger>
                      <SelectContent>
                        {problemStatements.map((ps) => (
                          <SelectItem key={ps._id} value={ps._id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{ps.title}</span>
                              <span className="text-xs text-gray-500">
                                {ps.category} • {ps.difficulty} • {getProblemStatementAssignmentCount(ps._id)} teams assigned
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {selectedProblemStatement && (
                      <div className="space-y-3">
                        {/* Show selected problem statement preview */}
                        {(() => {
                          const selectedPS = problemStatements.find(p => p._id === selectedProblemStatement);
                          return selectedPS ? (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                              <h5 className="font-medium text-blue-800 mb-2">Preview: {selectedPS.title}</h5>
                              <div className="space-y-1 text-sm">
                                <div><strong>Category:</strong> {selectedPS.category}</div>
                                <div><strong>Difficulty:</strong> {selectedPS.difficulty}</div>
                                <div><strong>Description:</strong></div>
                                <p className="text-gray-700 text-xs leading-relaxed">{selectedPS.description}</p>
                              </div>
                            </div>
                          ) : null;
                        })()}
                        
                        <Button
                          onClick={() => {
                            const ps = problemStatements.find(p => p._id === selectedProblemStatement);
                            if (ps) onAssignProblemStatement(team._id, ps);
                          }}
                          className="w-full bg-blue-600 hover:bg-blue-700"
                          disabled={team.status === 'submitted' || team.status === 'completed'}
                        >
                          <BookOpen className="h-4 w-4 mr-2" />
                          Assign Problem Statement
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="scoring" className="mt-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Score Submission</h3>
              {team.submission ? (
                <div className="space-y-6">
                  {/* Simple Scoring Form */}
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-800">Scoring</h4>
                      {team.submissionScore && (
                        <div className="text-sm text-gray-600">
                          Current Score: {team.submissionScore.score || 0}/170
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="simple-score">Score (0-170)</Label>
                        <Input
                          id="simple-score"
                          name="simple-score"
                          type="number"
                          min="0"
                          max="170"
                          value={submissionScore}
                          onChange={(e) => setSubmissionScore(Number(e.target.value))}
                          placeholder="Enter score..."
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="simple-feedback">Feedback (Optional)</Label>
                        <Textarea
                          id="simple-feedback"
                          name="simple-feedback"
                          value={submissionFeedback}
                          onChange={(e) => setSubmissionFeedback(e.target.value)}
                          placeholder="Provide feedback..."
                          rows={4}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="simple-files">Upload Files (Optional)</Label>
                        <div className="mt-2">
                          <Input
                            id="simple-files"
                            type="file"
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png,.gif"
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              setScoringFiles(files);
                            }}
                            className="cursor-pointer"
                          />
                          <p className="text-sm text-gray-500 mt-1">
                            Accepted formats: PDF, JPG, JPEG, PNG, GIF
                          </p>
                          {scoringFiles.length > 0 && (
                            <div className="mt-2 space-y-1">
                              <p className="text-sm font-medium">Selected files:</p>
                              {scoringFiles.map((file, index) => (
                                <div key={index} className="flex items-center justify-between text-sm text-gray-600 bg-gray-50 p-2 rounded">
                                  <span>{file.name}</span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setScoringFiles(scoringFiles.filter((_, i: number) => i !== index))}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <Button
                          onClick={() => onScoreSubmission(team._id, submissionScore, submissionFeedback)}
                          disabled={submissionScore < 0 || submissionScore > 170 || isScoringLoading}
                          className="w-full"
                        >
                          <Award className="h-4 w-4 mr-2" />
                          {isScoringLoading ? 'Submitting...' : 'Submit Score'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                  <p className="text-gray-600">No submission available for scoring</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="submission" className="mt-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Project Submission</h3>
              {team.submission ? (
                <div className="space-y-6">
                  {/* Project Information */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-3">Project Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <strong>Title:</strong> {team.submission.title}
                      </div>
                      <div>
                        <strong>Submitted:</strong> {new Date(team.submission.submittedAt).toLocaleString()}
                      </div>
                    </div>
                    <div className="mt-3">
                      <strong>Description:</strong>
                      <p className="text-gray-600 mt-1">{team.submission.description}</p>
                    </div>
                    {team.submission.generalRemarks && (
                      <div className="mt-3">
                        <strong>General Remarks:</strong>
                        <p className="text-gray-600 mt-1">{team.submission.generalRemarks}</p>
                      </div>
                    )}
                  </div>

                  {/* Presentation */}
                  {(team.submission.presentation?.file || team.submission.presentationLink) && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h4 className="font-semibold text-green-800 mb-3">Presentation</h4>
                      <div className="space-y-2">
                        {team.submission.presentation?.file && (
                          <div>
                            <strong>File:</strong> 
                            <div className="flex items-center gap-2 mt-1">
                              <FileText className="h-4 w-4 text-green-600" />
                              <span className="text-green-700">{team.submission.presentation.file.split('/').pop() || 'Presentation file uploaded'}</span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => team.submission?.presentation?.file && onDownloadFile(team.submission.presentation.file)}
                                className="ml-2"
                              >
                                <Download className="h-4 w-4 mr-1" />
                                Download
                              </Button>
                            </div>
                            {team.submission.presentation.remarks && (
                              <div className="mt-2">
                                <strong>Remarks:</strong>
                                <p className="text-gray-600 mt-1">{team.submission.presentation.remarks}</p>
                              </div>
                            )}
                          </div>
                        )}
                        {team.submission.presentationLink && (
                          <div>
                            <strong>Link:</strong> 
                            <a href={team.submission.presentationLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-2">
                              {team.submission.presentationLink}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* GitHub Repository */}
                  {(team.submission.githubRepository?.link || team.submission.githubLink) && (
                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <h4 className="font-semibold text-purple-800 mb-3">GitHub Repository</h4>
                      <div className="space-y-2">
                        <div>
                          <strong>Repository:</strong> 
                          <a href={team.submission.githubRepository?.link || team.submission.githubLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-2">
                            {team.submission.githubRepository?.link || team.submission.githubLink}
                          </a>
                        </div>
                        {team.submission.githubRepository?.remarks && (
                          <div>
                            <strong>Remarks:</strong>
                            <p className="text-gray-600 mt-1">{team.submission.githubRepository.remarks}</p>
                          </div>
                        )}
                        {team.submission.demoLink && (
                          <div>
                            <strong>Demo Link:</strong> 
                            <a href={team.submission.demoLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-2">
                              {team.submission.demoLink}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Additional Documents */}
                  {team.submission.additionalDocuments && team.submission.additionalDocuments.length > 0 && (
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <h4 className="font-semibold text-orange-800 mb-3">Additional Documents</h4>
                      <div className="space-y-3">
                        {team.submission.additionalDocuments.map((doc, index) => (
                          <div key={index} className="border border-orange-200 rounded p-3 bg-white">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-orange-600" />
                                <strong>{doc.name}</strong>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onDownloadFile(doc.file)}
                              >
                                <Download className="h-4 w-4 mr-1" />
                                Download
                              </Button>
                            </div>
                            {doc.remarks && (
                              <div className="mb-2">
                                <strong>Remarks:</strong>
                                <p className="text-gray-600 mt-1">{doc.remarks}</p>
                              </div>
                            )}
                            <div className="text-sm text-gray-600">
                              Uploaded: {new Date(doc.uploadedAt).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Submission Score */}
                  {team.submissionScore && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <h4 className="font-semibold text-yellow-800 mb-3">Submission Score</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <strong>Score:</strong> {team.submissionScore.score}/100
                        </div>
                        <div>
                                                      <strong>Scored By:</strong> {team.submissionScore.scoredBy?.firstName} {team.submissionScore.scoredBy?.lastName}
                        </div>
                        <div>
                          <strong>Scored At:</strong> {new Date(team.submissionScore.scoredAt).toLocaleString()}
                        </div>
                      </div>
                      {team.submissionScore.feedback && (
                        <div className="mt-3">
                          <strong>Feedback:</strong>
                          <p className="text-gray-600 mt-1">{team.submissionScore.feedback}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                  <p className="text-gray-600">No submission available</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 mt-6">
          {/* Action Buttons */}
          {team.status === 'pending_approval' && (
            <Button
              onClick={onApprove}
              className="bg-green-600 hover:bg-green-700"
            >
              <Check className="h-4 w-4 mr-2" />
              Approve Team
            </Button>
          )}
          
          {team.status !== 'dissolved' && (
            <Button
              variant="outline"
              onClick={() => onDeleteTeam(team._id, team.name)}
              className="text-red-600 hover:text-red-700 border-red-600"
            >
              <X className="h-4 w-4 mr-2" />
              Delete Team
            </Button>
          )}
          
          {/* Tab-specific buttons */}
          {activeTab === 'assignments' && canBeAssigned(team) && (
            <Button
              variant="outline"
              onClick={() => {
                if (selectedMentor) {
                  onAssignMentor(team._id, selectedMentor);
                }
              }}
              disabled={!selectedMentor || assigningMentor === team._id}
              className="text-purple-600 hover:text-purple-700 border-purple-600"
            >
              {assigningMentor === team._id ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                  Assigning...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Assign Mentor
                </>
              )}
            </Button>
          )}
          
          {activeTab === 'problem-statement' && canBeAssigned(team) && (
            <Button
              variant="outline"
              onClick={() => {
                if (selectedProblemStatement) {
                  const problemStatement = problemStatements.find(ps => ps._id === selectedProblemStatement);
                  if (problemStatement) {
                    onAssignProblemStatement(team._id, problemStatement);
                  }
                }
              }}
              disabled={!selectedProblemStatement}
              className="text-green-600 hover:text-green-700 border-green-600"
            >
              <BookOpen className="h-4 w-4 mr-2" />
              Assign Problem Statement
            </Button>
          )}
          
          {/* Removed duplicate Submit Score button - use the one in the scoring form instead */}
          
          {activeTab === 'submission' && team.submission && (
            <Button
              variant="outline"
              onClick={() => onToggleResubmission(team._id)}
              className={team.allowResubmission ? "text-red-600 hover:text-red-700 border-red-600" : "text-green-600 hover:text-green-700 border-green-600"}
            >
              {team.allowResubmission ? (
                <>
                  <X className="h-4 w-4 mr-2" />
                  Disable Submit / Re-Submit
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Allow Submit / Re-Submit
                </>
              )}
            </Button>
          )}
          
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TeamManagement; 